'use client';
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, CircularProgress, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowDownDoubleIcon, ArrowUpDoubleIcon, BubbleChatIcon, CellsIcon, File01Icon, LibraryIcon, Search01Icon } from 'hugeicons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlobalSearchModule } from '../../../application/common/enums/global-search-module';
import { useLazyGetGlobalSearchAllQuery } from '../../../application/features/global-search/global-search-api';
import { setFileResults } from '../../../application/features/global-search/globalSearchSlice';
import { useAppDispatch, useAppSelector } from '../../../application/store/useStore';
import useTranslate from '../../../localization/helpers';
import PageHeaderButton from '../../components/PageHeaderButton';
import ProtectedElement from '../../components/ProtectedElement';
import AssetChip from '../../layout/global-search/search-result/chip-content/assets-chip';
import ChatChip from '../../layout/global-search/search-result/chip-content/chat-chip';
import FileChipSummary from '../../layout/global-search/search-result/chip-content/file-chip-summary';
import KnowledgeChip from '../../layout/global-search/search-result/chip-content/knowledge-chip';
import { Permissions } from '../../permissions';
import SearchAccordion from './accordion';
import TechnicianChip from '../../layout/global-search/search-result/chip-content/technician-chip';
import AiUserIcon from '../../icons/components/ai-user';
var GlobalSearchPage = function () {
    var _a;
    var theme = useTheme();
    var dispatch = useAppDispatch();
    var isXs = useMediaQuery(theme.breakpoints.only('xs'));
    var isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    var _b = __read(useSearchParams(), 1), searchParams = _b[0];
    var currentUser = useAppSelector(function (state) { return state.auth; }).currentUser;
    var currentCompany = useAppSelector(function (state) { return state.common; }).currentCompany;
    var navigate = useNavigate();
    var translate = useTranslate().translate;
    var _c = __read(useState(''), 2), searchValue = _c[0], setSearchValue = _c[1];
    var query = searchParams.get('query');
    var module = searchParams.get('module');
    var indepth = searchParams.get('indepth');
    var isInDepthEnabled = indepth === 'true';
    var _d = __read(useState(null), 2), searchRequest = _d[0], setSearchRequest = _d[1];
    var _e = __read(useLazyGetGlobalSearchAllQuery(), 2), globalSearchAll = _e[0], _f = _e[1], allResults = _f.data, isAllFetching = _f.isFetching;
    var isCombinedFetching = isAllFetching;
    var _g = __read(useState((_a = {},
        _a[GlobalSearchModule.All] = false,
        _a[GlobalSearchModule.Assets] = false,
        _a[GlobalSearchModule.Knowledge] = false,
        _a[GlobalSearchModule.Chat] = false,
        _a[GlobalSearchModule.Technicians] = false,
        _a[GlobalSearchModule.Files] = false,
        _a)), 2), expandedModules = _g[0], setExpandedModules = _g[1];
    var visibleModules = [];
    if (allResults) {
        if (allResults.chatCount > 0)
            visibleModules.push(GlobalSearchModule.Chat);
        if (allResults.knowledgeCount > 0)
            visibleModules.push(GlobalSearchModule.Knowledge);
        if (allResults.assetCount > 0)
            visibleModules.push(GlobalSearchModule.Assets);
        if (allResults.technicianCount > 0)
            visibleModules.push(GlobalSearchModule.Technicians);
        if (isInDepthEnabled && (isInDepthEnabled || Number(module) === GlobalSearchModule.Files)) {
            visibleModules.push(GlobalSearchModule.Files);
        }
    }
    var allExpanded = visibleModules.length > 0 && visibleModules.every(function (module) { return expandedModules[module]; });
    var handleToggleExpandCollapse = function () {
        var newExpandedState = !allExpanded;
        var newModules = __assign({}, expandedModules);
        visibleModules.forEach(function (module) {
            newModules[module] = newExpandedState;
        });
        setExpandedModules(newModules);
    };
    var handleAccordionToggle = function (module) {
        setExpandedModules(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[module] = !prev[module], _a)));
        });
    };
    useEffect(function () {
        dispatch(setFileResults([]));
    }, [dispatch]);
    useEffect(function () {
        if (query && module && currentUser && currentUser.userId) {
            setSearchRequest({
                searchText: query,
                module: Number(module),
                enablePagination: false,
                userId: currentUser.userId
            });
        }
    }, [query, module, currentUser, isInDepthEnabled]);
    useEffect(function () {
        if (searchRequest) {
            globalSearchAll(searchRequest);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchRequest]);
    useEffect(function () {
        if (Number(module)) {
            var moduleNumber_1 = Number(module);
            if (allResults) {
                var hasResults = (moduleNumber_1 === GlobalSearchModule.Chat && allResults.chatCount > 0) ||
                    (moduleNumber_1 === GlobalSearchModule.Knowledge && allResults.knowledgeCount > 0) ||
                    (moduleNumber_1 === GlobalSearchModule.Assets && allResults.assetCount > 0) ||
                    (moduleNumber_1 === GlobalSearchModule.Technicians && allResults.technicianCount > 0) ||
                    (moduleNumber_1 === GlobalSearchModule.Files &&
                        (allResults.fileCount > 0 || isInDepthEnabled));
                if (hasResults) {
                    setExpandedModules(function (prev) {
                        var _a;
                        return (__assign(__assign({}, prev), (_a = {}, _a[moduleNumber_1] = true, _a)));
                    });
                }
            }
        }
        if ((isInDepthEnabled && Number(module) === GlobalSearchModule.Files) ||
            (allResults && allResults.fileCount > 0)) {
            setExpandedModules(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[GlobalSearchModule.Files] = true, _a)));
            });
        }
    }, [module, allResults, isInDepthEnabled]);
    var handleSearchButtonClick = function () {
        var indepthParam = isInDepthEnabled ? '&indepth=true' : '';
        navigate("/search?query=".concat(searchValue, "&module=").concat(Number(module)).concat(indepthParam));
    };
    useEffect(function () {
        if (currentCompany && query) {
            if (query && module && currentUser && currentUser.userId) {
                setSearchRequest({
                    searchText: query,
                    module: Number(module),
                    enablePagination: false,
                    userId: currentUser.userId
                });
            }
        }
    }, [currentCompany, currentUser, module, query, isInDepthEnabled]);
    return (_jsxs(Box, { sx: {
            width: '100%',
            padding: isXs ? '16px' : '8px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }, children: [_jsxs(Box, { display: 'flex', gap: 1, alignItems: 'center', mb: { xs: 1, md: 2 }, children: [_jsx(Typography, { variant: 'h3', color: '#009983', children: translate('globalSearch.searchResults') }), Number(module) === GlobalSearchModule.All && (_jsx(PageHeaderButton, { text: allExpanded
                            ? translate('globalSearch.collapseAll')
                            : translate('globalSearch.expandAll'), endIcon: allExpanded ? _jsx(ArrowUpDoubleIcon, {}) : _jsx(ArrowDownDoubleIcon, {}), size: isXs ? 'small' : 'medium', variant: 'contained', color: 'primary', title: 'Toggle All', onClick: handleToggleExpandCollapse }))] }), _jsxs(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', gap: '20px', children: [_jsx(TextField, { size: 'small', variant: 'outlined', placeholder: translate('actions.search'), value: searchValue, onChange: function (event) { return setSearchValue(event.target.value); }, sx: { flex: '1 1 auto' } }), _jsx(Button, { variant: 'soft', startIcon: _jsx(Search01Icon, { color: 'primary', width: '20px', height: '20px' }), onClick: handleSearchButtonClick, children: translate('actions.search') })] }), allResults &&
                allResults.totalCount === 0 &&
                module &&
                !(Number(module) === GlobalSearchModule.All || Number(module) === GlobalSearchModule.Files) ? (_jsx(_Fragment, { children: _jsx(Box, { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingY: '200px', children: _jsx(Typography, { variant: 'h3', children: translate('common.noRecordFound') }) }) })) : isCombinedFetching ? (_jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', children: _jsx(CircularProgress, {}) })) : (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, mt: 2, overflow: 'auto', maxHeight: isXs || isIpad ? 'calc(100vh - 261px)' : 'calc(100vh - 200px)', children: [allResults && allResults.assetCount > 0 && allResults.assets.length > 0 && (_jsx(ProtectedElement, { permissions: [Permissions.Machines.Read], action: 'hide', children: _jsx(SearchAccordion, { title: translate('common.assets'), icon: _jsx(CellsIcon, { color: theme.palette.primary.main }), content: _jsx(AssetChip, { data: allResults.assets, count: allResults.assetCount, searchTerm: query !== null && query !== void 0 ? query : '', isShowViewAll: false }), expanded: expandedModules[GlobalSearchModule.Assets], onToggle: function () { return handleAccordionToggle(GlobalSearchModule.Assets); } }, "assets-accordion") })), allResults && allResults.knowledgeCount > 0 && allResults.knowledges.length > 0 && (_jsx(ProtectedElement, { permissions: [Permissions.Knowledge.Read], action: 'hide', children: _jsx(SearchAccordion, { title: translate('common.knowledge'), icon: _jsx(LibraryIcon, { color: theme.palette.primary.main }), content: _jsx(KnowledgeChip, { data: allResults.knowledges, count: allResults.knowledgeCount, searchTerm: query !== null && query !== void 0 ? query : '', isShowViewAll: false }), expanded: expandedModules[GlobalSearchModule.Knowledge], onToggle: function () { return handleAccordionToggle(GlobalSearchModule.Knowledge); } }, "knowledge-accordion") })), allResults && allResults.chatCount > 0 && allResults.chats.length > 0 && (_jsx(SearchAccordion, { title: translate('common.chats'), icon: _jsx(BubbleChatIcon, { color: theme.palette.primary.main }), content: _jsx(ChatChip, { data: allResults.chats, count: allResults.chatCount, searchTerm: query !== null && query !== void 0 ? query : '', isShowViewAll: false }), expanded: expandedModules[GlobalSearchModule.Chat], onToggle: function () { return handleAccordionToggle(GlobalSearchModule.Chat); } }, "chat-accordion")), allResults && allResults.technicianCount > 0 && allResults.technicians.length > 0 && (_jsx(ProtectedElement, { permissions: [Permissions.Technicians.Read], action: 'hide', children: _jsx(SearchAccordion, { title: translate('common.technicians'), icon: _jsx(AiUserIcon, { color: theme.palette.primary.main }), content: _jsx(TechnicianChip, { data: allResults.technicians, count: allResults.technicianCount, searchTerm: query !== null && query !== void 0 ? query : '', isShowViewAll: false }), expanded: expandedModules[GlobalSearchModule.Technicians], onToggle: function () { return handleAccordionToggle(GlobalSearchModule.Technicians); } }, "technicians-accordion") })), allResults &&
                        (allResults.fileCount > 0 ||
                            isInDepthEnabled ||
                            Number(module) === GlobalSearchModule.Files) && (_jsx(ProtectedElement, { permissions: [Permissions.Knowledge.Read], action: 'hide', children: _jsx(SearchAccordion, { title: translate('common.indepth'), icon: _jsx(File01Icon, { color: theme.palette.primary.main }), content: _jsx(FileChipSummary, { searchTerm: query !== null && query !== void 0 ? query : '', isShowViewAll: false, iconWidth: '1%' }), expanded: expandedModules[GlobalSearchModule.Files], onToggle: function () { return handleAccordionToggle(GlobalSearchModule.Files); } }, "file-accordion") }))] }))] }));
};
export default GlobalSearchPage;
